import { Box, List, ListItem, Skeleton, Typography } from '@mui/material';
import React, { useState } from 'react';

import { DriverScore } from '../../../../backendsdk';
import { ScoreAvatar } from '../../../../components/ScoreAvatar';
import { TrackedListItemButton as ListItemButton } from '../../../../components/TrackedComponents';
import { getDriverDisplayName } from '../../../../hooks/drivers';
import useProfile from '../../../../hooks/profile';
import { driversPageDefs, flattenRoles } from '../../../../utils/Pages';
import palette from '../../../ColorPalette';
import DriverModal from '../../Drivers/DriverModal';

interface DriverListProps {
    loading: boolean;
    header: string;
    scoreType: 'safety' | 'wellness';
    drivers: DriverScore[];
    order: 'asc' | 'desc';
}

const DriverList: React.FC<DriverListProps> = (props: DriverListProps) => {
    const [selectedDriverToken, setSelectedDriverToken] = useState<number>();

    const sortedDrivers = props.drivers.sort((a, b) => {
        if (props.order === 'asc') {
            return a.score - b.score;
        } else {
            return b.score - a.score;
        }
    });
    const { profile } = useProfile();
    const showDriverCode = profile.customer.settings.show_driver_code ?? false;
    const hasLinkPermissions = flattenRoles(driversPageDefs).includes(profile.role);

    return (
        <Box
            sx={{
                width: '100%',
                height: 245,
                display: 'flex',
                flexDirection: 'column',
                borderRight: props.order === 'asc' ? `1px solid ${palette.neutral[400]}` : undefined,
            }}
        >
            {selectedDriverToken !== undefined ? (
                <DriverModal
                    driverToken={selectedDriverToken}
                    onClose={() => setSelectedDriverToken(undefined)}
                    initialTab={1}
                />
            ) : null}
            <Typography fontSize={14} sx={{ fontWeight: 'bold', px: 1, mb: 0.5 }}>
                {props.header}
            </Typography>
            {props.loading ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 1 }}>
                    {[...Array(5).keys()].map((key) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                height: 44,
                            }}
                        >
                            <Skeleton variant="rounded" animation="wave" sx={{ height: 21, width: 150 }} />
                            <Skeleton variant="rounded" animation="wave" sx={{ height: 28, width: 28 }} />
                        </Box>
                    ))}
                </Box>
            ) : (
                <List disablePadding>
                    {sortedDrivers.map((driver, idx) => {
                        const trimmedName = getDriverDisplayName(driver.driver, showDriverCode);
                        const content = (
                            <>
                                <Typography fontSize={14}>{trimmedName}</Typography>
                                <ScoreAvatar score={driver.score} />
                            </>
                        );

                        if (hasLinkPermissions) {
                            return (
                                <ListItemButton
                                    key={`bottom-safety-${driver.driver.token}-${idx}`}
                                    id={`${props.order === 'asc' ? 'top' : 'bottom'}-${props.scoreType}-${idx}`}
                                    data-testid={`${props.order === 'asc' ? 'bottom' : 'top'}-${
                                        props.scoreType
                                    }-${idx}`}
                                    disableGutters
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        p: 1,
                                    }}
                                    onClick={() => setSelectedDriverToken(driver.driver.token)}
                                >
                                    {content}
                                </ListItemButton>
                            );
                        }
                        return (
                            <ListItem
                                key={`bottom-safety-${driver.driver.token}-${idx}`}
                                data-testid={`top-${props.scoreType}-${idx}`}
                                disableGutters
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    '&:hover': {
                                        backgroundColor: palette.neutral[100],
                                    },
                                }}
                            >
                                {content}
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

export default DriverList;
